import { Dispatch, forwardRef, InputHTMLAttributes, SetStateAction } from "react";
import { toast } from "react-toastify";
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import { Box, Input, SvgIcon, useTheme } from "@mui/material";
import { Check, Close, CropSquare, Settings, Square } from "@mui/icons-material";
import { GridCellParams } from "@mui/x-data-grid";
import { ReactComponent as halfSquareIcon } from "../common/icons/halfSquare.svg"
import { format, isBefore, isToday, parseISO, isValid } from 'date-fns';
import nbLocale from 'date-fns/locale/nb';

export function onlyUniqueListFilter(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
}
export function sortDateList(a: string, b: string) {
    return new Date(b).getTime() - new Date(a).getTime()
}

export function amountError(amount: string | undefined) {
    if (amount && amount !== "" && !/^\d+(,\d+)?$/.test(amount)) {
        return true;
    } else {
        return false;
    }
}
export function parseIntOrNull(value: string) {
    let newValue = parseInt(value);
    if (isNaN(newValue)) {
        return null;
    } else {
        return newValue;
    }
}
export function parseNbNOFloatOrNull(value: string) {
    let newValue = parseFloat(value.replace(",","."));
    if (isNaN(newValue)) {
        return null;
    } else {
        return newValue;
    }
}

//Convert rem to pixels based on currect browser fontSize.
export function remToPixels(rem: number) {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}
//#######################################################################
//#######################Adding Space to numbers#########################
export function numberWithSpaces(x: number) {
    if (x != null && x != undefined) {
        var parts = x.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return parts.join(".");
    }
    return null
}
//#######################################################################
//#####################Round to two decimal places#######################
export function numberRoundTwoDecimals(x: number) {
    return Math.round((x + Number.EPSILON) * 100) / 100;
}
//#######################################################################
//################Adding Space to two decimal numbers####################
export function numberWithSpacesAndTwoDecimals(x: number) {
    if (x != null && x != undefined) {
        let v = numberRoundTwoDecimals(x);
        var parts = v.toString().split(".");
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        if (parts[1].length === 1)
            parts[1] = "0" + parts[1];
        return parts.join(".");
    }
    return null
}
//#######################################################################
//######################Random number gen################################
function xmur3(str: string) {
    for (var i = 0, h = 1779033703 ^ str.length; i < str.length; i++) {
        h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
        h = h << 13 | h >>> 19;
    } return function () {
        h = Math.imul(h ^ (h >>> 16), 2246822507);
        h = Math.imul(h ^ (h >>> 13), 3266489909);
        return (h ^= h >>> 16) >>> 0;
    }
}
function sfc32(a: number, b: number, c: number, d: number) {
    return function () {
        a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
        var t = (a + b) | 0;
        a = b ^ b >>> 9;
        b = c + (c << 3) | 0;
        c = (c << 21 | c >>> 11);
        d = d + 1 | 0;
        t = t + d | 0;
        c = c + t | 0;
        return (t >>> 0) / 4294967296;
    }
}
export function randWithSeed(seedVar: string) {
    var seed = xmur3(seedVar);
    var rand = sfc32(seed(), seed(), seed(), seed());
    return rand;
}
export function randGenSeed(rand: () => number, min: number, max: number) {
    return Math.floor(rand() * (max - min)) + min;
}
//#######################################################################
export function onlyNumbers(newNumber: string, setState: Dispatch<SetStateAction<string>>) {
    if (newNumber !== "" && !/^\d+$/.test(newNumber)) {
        toast("Kan bare bruke positive tall i dette tekstfeltet!");
    } else {
        setState(newNumber);
    }
}
export function onlyPercent(newNumber: string, setState: Dispatch<SetStateAction<string>>) {
    if (newNumber !== "" && !/^\d+$/.test(newNumber)) {
        toast("Kan bare bruke positive tall i dette tekstfeltet!");
    } else if (parseInt(newNumber) === 0 || parseInt(newNumber) > 100) {
        toast("Prosenten kan ikke v�re 0 eller over 100!");
    } else {
        setState(newNumber);
    }
}
export function padLeadingZerosToString(num: string, size: number) {
    while (num.length < size) num = "0" + num;
    return num;
}
export function padLeadingZerosToNum(num: number, size: number) {
    let textNum = num.toString();
    while (textNum.length < size) textNum = "0" + num;
    return textNum;
}

interface CustomNumericDecimalLocaleProps {
    onChange: (event: { target: { value: string } }) => void,
}
export const NumericFormatDecimalLocale = forwardRef<NumericFormatProps, CustomNumericDecimalLocaleProps>(
    function NumericFormatDecimalLocale(props, ref) {
        const theme = useTheme();
        const { onChange, ...other } = props;
        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            value: values.value,
                        },
                    });
                }}
                decimalSeparator=","
                valueIsNumericString
            />
        );
    },
);
export const NumericFormatWhole = forwardRef<NumericFormatProps, CustomNumericDecimalLocaleProps>(
    function NumericFormatWhole(props, ref) {
        const { onChange, ...other } = props;
        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            value: values.value,
                        },
                    });
                }}
                decimalScale={0}
                valueIsNumericString
            />
        );
    },
);

export const StatusIconVoucherRegistration = ({ status }: { status: string }): JSX.Element => {
    switch (status) {
        case "Transferred": //Avhuking
            return <Check fontSize="medium" color="success" />;
        case "Rejected": //Kryss
            return <Close fontSize="medium" color="error" />;
        case "AwaitingForDataEntry": //TomFirkant
            return <CropSquare fontSize="medium" color="primary" />;
        case "InProgress": //Halvfirkant
            return <SvgIcon component={halfSquareIcon} color="primary" sx={{ fontSize: "1rem" }} inheritViewBox />;
        case "AwaitingForTransfer": //Fullfirkant
            return <Square sx={{ fontSize: "1.2rem" }} color="primary" />;
        default:
            return <>N/A</>;
    }
}

export const StatusIcon = ({ status }: { status: string }): JSX.Element => {
    switch (status) {
        case "Transferred": //Avhuking
            return <Check fontSize="medium" color="success" />;
        case "AdditionTransferred": //Avhuking
            return <Check fontSize="medium" color="success" />;
        case "ApprovalPending": //Tannhjul
            return <Settings fontSize="medium" color="info" />;
        case "ApprovalPostponed": //Tannhjul
            return <Settings fontSize="medium" color="info" />;
        case "AwaitingForDataEntry": //Tannhjul
            return <Settings fontSize="medium" color="info" />;
        case "AwaitingForTransfer": //Tannhjul
            return <Settings fontSize="medium" color="info" />;
        case "InProgress": //Tannhjul
            return <Settings fontSize="medium" color="info" />;
        case "Rejected": //Kryss
            return <Close fontSize="medium" color="error" />;
        default:
            return <></>;
    }
}

export const DueDateWithColor = ({ params }: { params: GridCellParams }): JSX.Element => {
    const theme = useTheme();
    const today = new Date();

    const dueDate = params.value ? new Date(params.value) : null;

    if (!dueDate) {
        return (
            <Box
                height="100%"
                width="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
                bgcolor={theme.palette.background.paper}
                color={theme.palette.text.primary}
            >
                {""}
            </Box>
        );
    }

    let color = theme.palette.info.main;
    if (dueDate < today) {
        color = theme.palette.error.main;
    } else if (dueDate.toDateString() === today.toDateString()) {
        color = theme.palette.warning.main;
    }

    return (
        <Box
            height="100%"
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            bgcolor={color}
            color={theme.palette.background.paper}
        >
            {format(dueDate, 'dd.MM.yyyy', { locale: nbLocale })}
        </Box>
    );
};

export function RoundNumAccounting(num: number, length: number) {
    var number = Math.round(num * Math.pow(10, length)) / Math.pow(10, length);
    return number;
}

//export const NumericFormatDecimalLocale = (props: any) => {
//    return <NumericFormat
//        decimalSeparator=","
//        valueIsNumericString
//        {...props}
//    />
//}